<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="tw-fixed tw-inset-0 tw-z-50 tw-overflow-y-auto"
      @keydown.esc="$emit('close')"
    >
      <div class="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-p-4">
        <div
          class="tw-fixed tw-inset-0 tw-bg-slate-900 tw-bg-opacity-60 tw-transition-opacity"
          @click="$emit('close')"
        />
        <div class="tw-relative tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-max-w-4xl tw-w-full tw-max-h-[90vh] tw-flex tw-flex-col">
          <!-- Modal header -->
          <div class="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-border-b tw-border-slate-200">
            <h3 class="tw-text-lg tw-font-semibold tw-text-slate-900">
              Delivery &amp; Collections Guidelines
            </h3>
            <button
              type="button"
              class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-slate-100 tw-text-slate-600 tw-h-9 tw-w-9 hover:tw-bg-slate-200"
              aria-label="Close delivery guidelines modal"
              @click="$emit('close')"
            >
              <span class="tw-text-lg">×</span>
            </button>
          </div>

          <!-- PDF container with scroll tracking -->
          <div
            ref="pdfScrollContainer"
            class="tw-flex-1 tw-overflow-y-auto tw-min-h-0 tw-bg-slate-100"
            style="height: calc(90vh - 180px);"
            @scroll="handlePdfScroll"
          >
            <!-- Loading state -->
            <div
              v-if="pdfLoading"
              class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20"
            >
              <div class="tw-h-10 tw-w-10 tw-border-3 tw-border-blue-500 tw-border-t-transparent tw-rounded-full tw-animate-spin" />
              <p class="tw-mt-4 tw-text-sm tw-text-slate-600">
                Loading PDF...
              </p>
            </div>

            <!-- Error state -->
            <div
              v-else-if="pdfError"
              class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20 tw-px-6 tw-text-center"
            >
              <svg
                class="tw-h-12 tw-w-12 tw-text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p class="tw-mt-4 tw-text-sm tw-text-slate-700 tw-font-medium">
                Unable to load PDF
              </p>
              <p class="tw-mt-1 tw-text-xs tw-text-slate-500">
                {{ pdfError }}
              </p>
              <a
                :href="deliveryUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="tw-mt-4 tw-text-sm tw-text-blue-600 hover:tw-underline"
              >
                Open PDF in new tab
              </a>
            </div>

            <!-- PDF pages rendered by pdf.js -->
            <div
              v-else
              ref="pdfPagesContainer"
              class="tw-py-4 tw-space-y-4"
            >
              <div
                v-for="page in pdfRenderedPages"
                :key="page.pageNum"
                class="tw-flex tw-justify-center tw-px-4"
              >
                <canvas
                  :id="'delivery-pdf-canvas-' + page.pageNum"
                  class="tw-shadow-lg tw-bg-white tw-max-w-full"
                />
              </div>
            </div>
          </div>

          <!-- Modal footer with progress and confirm button -->
          <div class="tw-px-6 tw-py-4 tw-border-t tw-border-slate-200 tw-bg-slate-50 tw-rounded-b-2xl">
            <div class="tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-between tw-gap-4">
              <div class="tw-flex tw-items-center tw-gap-3">
                <div class="tw-flex tw-items-center tw-gap-2">
                  <div class="tw-h-2 tw-w-32 tw-bg-slate-200 tw-rounded-full tw-overflow-hidden">
                    <div
                      class="tw-h-full tw-bg-blue-600 tw-transition-all tw-duration-300"
                      :style="{ width: pdfScrollProgress + '%' }"
                    />
                  </div>
                  <span class="tw-text-xs tw-text-slate-500">{{ Math.round(pdfScrollProgress) }}%</span>
                </div>
                <span
                  v-if="pdfTotalPages > 0"
                  class="tw-text-xs tw-text-slate-500"
                >
                  Page {{ pdfCurrentPage }} of {{ pdfTotalPages }}
                </span>
              </div>
              <div class="tw-flex tw-items-center tw-gap-3">
                <div
                  v-if="!hasScrolledToBottom"
                  class="tw-flex tw-items-center tw-gap-2 tw-text-amber-600"
                >
                  <svg
                    class="tw-h-4 tw-w-4 tw-animate-bounce"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 14l-7 7m0 0l-7-7m7 7V3"
                    />
                  </svg>
                  <span class="tw-text-xs tw-font-medium">Scroll to bottom</span>
                </div>
                <div
                  v-else
                  class="tw-flex tw-items-center tw-gap-2 tw-text-emerald-600"
                >
                  <svg
                    class="tw-h-4 tw-w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span class="tw-text-xs tw-font-medium">Ready</span>
                </div>
                <button
                  type="button"
                  class="tw-inline-flex tw-justify-center tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 hover:tw-bg-slate-50"
                  @click="$emit('close')"
                >
                  Close
                </button>
                <button
                  type="button"
                  class="tw-inline-flex tw-justify-center tw-rounded-md tw-border tw-border-transparent tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2 tw-transition-colors"
                  :class="!hasScrolledToBottom ? 'tw-bg-slate-300 tw-cursor-not-allowed' : 'tw-bg-blue-600 hover:tw-bg-blue-700'"
                  :disabled="!hasScrolledToBottom"
                  @click="handleAccept"
                >
                  I accept the instructions
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { ref, watch, nextTick, onBeforeUnmount } from 'vue'

export default {
  name: 'DeliveryGuidelinesModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    deliveryUrl: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'accept'],
  setup(props, { emit }) {
    const pdfScrollContainer = ref(null)
    const pdfPagesContainer = ref(null)
    const pdfLoading = ref(false)
    const pdfError = ref('')
    const pdfRenderedPages = ref([])
    const pdfTotalPages = ref(0)
    const pdfCurrentPage = ref(1)
    const pdfScrollProgress = ref(0)
    const hasScrolledToBottom = ref(false)

    let pdfDoc = null
    let pdfJsLib = null

    // Load pdf.js library dynamically
    const loadPdfJs = () => {
      return new Promise((resolve, reject) => {
        if (window.pdfjsLib) {
          resolve(window.pdfjsLib)
          return
        }

        const script = document.createElement('script')
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'
        script.async = true
        script.onload = () => {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'
          resolve(window.pdfjsLib)
        }
        script.onerror = () => reject(new Error('Failed to load PDF.js'))
        document.head.appendChild(script)
      })
    }

    // Render a single PDF page to canvas
    const renderPage = async (pageNum) => {
      if (!pdfDoc) return

      try {
        const page = await pdfDoc.getPage(pageNum)
        const canvas = document.getElementById('delivery-pdf-canvas-' + pageNum)
        if (!canvas) return

        const scale = 1.5
        const viewport = page.getViewport({ scale })
        const outputScale = window.devicePixelRatio || 1

        canvas.width = Math.floor(viewport.width * outputScale)
        canvas.height = Math.floor(viewport.height * outputScale)
        canvas.style.width = Math.floor(viewport.width) + 'px'
        canvas.style.height = Math.floor(viewport.height) + 'px'

        const context = canvas.getContext('2d')
        context.scale(outputScale, outputScale)

        await page.render({
          canvasContext: context,
          viewport: viewport,
          intent: 'display'
        }).promise
      } catch (err) {
        console.error('Error rendering PDF page', pageNum, err)
      }
    }

    // Load and render PDF
    const loadPdf = async () => {
      if (!props.deliveryUrl) {
        pdfError.value = 'Delivery guidelines URL not configured'
        return
      }

      pdfLoading.value = true
      pdfError.value = ''
      pdfRenderedPages.value = []
      hasScrolledToBottom.value = false
      pdfScrollProgress.value = 0

      try {
        pdfJsLib = await loadPdfJs()

        const loadingTask = pdfJsLib.getDocument({
          url: props.deliveryUrl,
          cMapUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/',
          cMapPacked: true,
          standardFontDataUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/standard_fonts/'
        })
        pdfDoc = await loadingTask.promise
        pdfTotalPages.value = pdfDoc.numPages

        pdfRenderedPages.value = Array.from({ length: pdfDoc.numPages }, (_, i) => ({
          pageNum: i + 1
        }))

        pdfLoading.value = false

        await nextTick()
        await new Promise(resolve => setTimeout(resolve, 100))

        for (let i = 1; i <= pdfDoc.numPages; i++) {
          await renderPage(i)
        }
      } catch (err) {
        console.error('Failed to load PDF:', err)
        pdfError.value = err.message || 'Failed to load the PDF document'
        pdfLoading.value = false
      }
    }

    // Re-render PDF pages to existing canvases
    const rerenderPdf = async () => {
      if (!pdfDoc) return

      await nextTick()
      await new Promise(resolve => setTimeout(resolve, 100))

      for (let i = 1; i <= pdfDoc.numPages; i++) {
        await renderPage(i)
      }
    }

    // Handle scroll on PDF container
    const handlePdfScroll = () => {
      const container = pdfScrollContainer.value
      if (!container) return

      const scrollTop = container.scrollTop
      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight

      const maxScroll = scrollHeight - clientHeight
      if (maxScroll > 0) {
        pdfScrollProgress.value = Math.min(100, (scrollTop / maxScroll) * 100)
      } else {
        pdfScrollProgress.value = 100
      }

      if (pdfTotalPages.value > 0) {
        const pageHeight = scrollHeight / pdfTotalPages.value
        pdfCurrentPage.value = Math.min(
          pdfTotalPages.value,
          Math.floor(scrollTop / pageHeight) + 1
        )
      }

      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50
      if (isAtBottom && !hasScrolledToBottom.value) {
        hasScrolledToBottom.value = true
      }
    }

    const handleAccept = () => {
      if (!hasScrolledToBottom.value) return
      emit('accept')
      emit('close')
    }

    // Watch for modal open/close
    watch(() => props.show, async (isOpen) => {
      if (isOpen) {
        document.body.style.overflow = 'hidden'

        if (!pdfDoc) {
          await loadPdf()
        } else {
          await rerenderPdf()
        }

        nextTick(() => {
          if (pdfScrollContainer.value) {
            pdfScrollContainer.value.scrollTop = 0
          }
        })
      } else {
        document.body.style.overflow = ''
      }
    })

    onBeforeUnmount(() => {
      document.body.style.overflow = ''
    })

    return {
      pdfScrollContainer,
      pdfPagesContainer,
      pdfLoading,
      pdfError,
      pdfRenderedPages,
      pdfTotalPages,
      pdfCurrentPage,
      pdfScrollProgress,
      hasScrolledToBottom,
      handlePdfScroll,
      handleAccept
    }
  }
}
</script>
